/**
 * src/marketing/shell.tsx - chrome for the getabeam.com marketing site (web-only).
 *
 * Renders the sticky top nav (Beam logo + links + sign-in / start-free CTAs), a scrolling
 * content area, and the footer. Pages pass their body as children. In-page anchor links
 * ("/#features") scroll the shell's ScrollView to a registered <Section id="features">.
 *
 * This is marketing chrome only - it never touches the authed app. Tokens come from
 * src/theme; the few layout constants here (max content width, nav height) are marketing-
 * specific and live with the marketing code (this dir is outside the app style-guard scope,
 * but we still avoid raw hex/fonts and use theme tokens for all color/type).
 *
 * NEVER imports @smartbox/* (mobile invariant 1).
 */
import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';
import { Pressable, ScrollView, StyleSheet, View, useWindowDimensions } from 'react-native';
import { useRouter } from 'expo-router';
import { Text } from '../components/ui';
import { BeamLogo } from '../components/brand/Logo';
import { theme } from '../theme';
import { NAV_LINKS, FOOTER, SITE } from './content';

export const MAX_W = 1120;
const NAV_BREAK = 760; // below this, collapse nav links (logo + CTAs only)

// --- In-page section scrolling -------------------------------------------------------------

type NavCtx = {
  register: (id: string, y: number) => void;
  scrollTo: (id: string) => void;
  /** Navigate to a route path or in-page anchor ("/#features"). */
  navigate: (href: string) => void;
};
const MarketingNav = createContext<NavCtx>({ register: () => {}, scrollTo: () => {}, navigate: () => {} });
export const useMarketingNav = () => useContext(MarketingNav);

// --- Link primitive ------------------------------------------------------------------------

export function NavLink({
  label,
  href,
  onNavigate,
  emphasis = 'text',
}: {
  label: string;
  href: string;
  onNavigate: (href: string) => void;
  emphasis?: 'text' | 'primary' | 'outline';
}) {
  if (emphasis === 'primary' || emphasis === 'outline') {
    const filled = emphasis === 'primary';
    return (
      <Pressable
        accessibilityRole="link"
        onPress={() => onNavigate(href)}
        style={({ pressed }) => [
          styles.ctaBtn,
          filled
            ? { backgroundColor: pressed ? theme.palette.blueprint : theme.color.action.primary }
            : { borderWidth: theme.layout.hairline, borderColor: theme.color.border.strong, backgroundColor: pressed ? theme.color.bg.subtle : 'transparent' },
        ]}
      >
        <Text variant="bodyStrong" color={filled ? 'onPrimary' : 'primary'}>
          {label}
        </Text>
      </Pressable>
    );
  }
  return (
    <Pressable accessibilityRole="link" onPress={() => onNavigate(href)} style={styles.navLink}>
      <Text variant="body" color="secondary">
        {label}
      </Text>
    </Pressable>
  );
}

// --- Shell ---------------------------------------------------------------------------------

export function MarketingShell({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { width } = useWindowDimensions();
  const compact = width < NAV_BREAK;
  const scrollRef = useRef<ScrollView>(null);
  const offsets = useRef<Record<string, number>>({});

  const register = useCallback((id: string, y: number) => {
    offsets.current[id] = y;
  }, []);
  const scrollTo = useCallback((id: string) => {
    const y = offsets.current[id];
    if (typeof y === 'number') scrollRef.current?.scrollTo({ y: Math.max(0, y - 72), animated: true });
  }, []);

  const navigate = useCallback(
    (href: string) => {
      const hash = href.startsWith('/#') ? href.slice(2) : href.startsWith('#') ? href.slice(1) : null;
      if (hash) {
        if (offsets.current[hash] !== undefined) scrollTo(hash);
        else router.push('/');
        return;
      }
      router.push(href as never);
    },
    [router, scrollTo],
  );

  const ctx = useMemo<NavCtx>(() => ({ register, scrollTo, navigate }), [register, scrollTo, navigate]);

  return (
    <MarketingNav.Provider value={ctx}>
      <View style={styles.root}>
        {/* Sticky top nav */}
        <View style={styles.nav}>
          <View style={[styles.navInner, { maxWidth: MAX_W }]}>
            <Pressable accessibilityRole="link" onPress={() => navigate('/')}>
              <BeamLogo size={26} />
            </Pressable>
            {!compact && (
              <View style={styles.navLinks}>
                {NAV_LINKS.map((l) => (
                  <NavLink key={l.label} label={l.label} href={l.href} onNavigate={navigate} />
                ))}
              </View>
            )}
            <View style={styles.navCtas}>
              <NavLink label="Sign in" href="/login" onNavigate={navigate} emphasis="outline" />
              <NavLink label="Get started" href="/pricing" onNavigate={navigate} emphasis="primary" />
            </View>
          </View>
        </View>

        <ScrollView
          ref={scrollRef}
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {children}

          {/* Footer */}
          <View style={styles.footer}>
            <View style={[styles.footerInner, { maxWidth: MAX_W }]}>
              <View style={styles.footerBrand}>
                <BeamLogo size={24} />
                <Text variant="small" color="secondary" style={styles.footerBlurb}>
                  {SITE.blurb}
                </Text>
              </View>
              <View style={styles.footerCols}>
                {FOOTER.columns.map((col) => (
                  <View key={col.title} style={styles.footerCol}>
                    <Text variant="label" color="secondary">
                      {col.title}
                    </Text>
                    {col.links.map((l) => (
                      <Pressable key={l.label} accessibilityRole="link" onPress={() => navigate(l.href)} style={styles.footerLink}>
                        <Text variant="small" color="secondary">
                          {l.label}
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                ))}
              </View>
            </View>
            <View style={[styles.footerLegal, { maxWidth: MAX_W }]}>
              <Text variant="small" color="secondary">
                {FOOTER.legal}
              </Text>
            </View>
          </View>
        </ScrollView>
      </View>
    </MarketingNav.Provider>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: theme.color.bg.app },
  nav: {
    borderBottomWidth: theme.layout.hairline,
    borderBottomColor: theme.color.border.default,
    backgroundColor: theme.color.bg.app,
    paddingHorizontal: theme.spacing.lg,
  },
  navInner: {
    width: '100%',
    alignSelf: 'center',
    height: 64,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.lg,
  },
  navLinks: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.xs, flex: 1, marginLeft: theme.spacing.xl },
  navLink: { paddingVertical: theme.spacing.sm, paddingHorizontal: theme.spacing.md, borderRadius: theme.radius.sm },
  navCtas: { flexDirection: 'row', alignItems: 'center', gap: theme.spacing.sm },
  ctaBtn: { height: 40, paddingHorizontal: theme.spacing.lg, borderRadius: theme.radius.md, alignItems: 'center', justifyContent: 'center' },
  scroll: { flex: 1 },
  scrollContent: { flexGrow: 1 },
  footer: {
    marginTop: theme.spacing['4xl'],
    borderTopWidth: theme.layout.hairline,
    borderTopColor: theme.color.border.default,
    backgroundColor: theme.color.bg.surface,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing['4xl'],
  },
  footerInner: {
    width: '100%',
    alignSelf: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: theme.spacing['3xl'],
  },
  footerBrand: { flexGrow: 1, flexBasis: 280, maxWidth: 360, gap: theme.spacing.md },
  footerBlurb: { lineHeight: 20 },
  footerCols: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing['3xl'] },
  footerCol: { gap: theme.spacing.sm, minWidth: 120 },
  footerLink: { paddingVertical: theme.spacing.xs },
  footerLegal: {
    width: '100%',
    alignSelf: 'center',
    marginTop: theme.spacing['3xl'],
    paddingTop: theme.spacing.lg,
    borderTopWidth: theme.layout.hairline,
    borderTopColor: theme.color.border.default,
  },
});
